import { Icon } from "@iconify/react";
import { Col, Modal, Row, Space, Spin, Typography } from "antd";
import React, { useState } from "react";
import { api, get, post, put } from "@services/api.service";
import { PDFViewer } from "../PDFViewer/PDFViewer";
import { isEmpty } from "lodash";
import { docVersionKey } from "~/Utils";
import { useHasAccess } from "@hooks/useHasAccess";
import { useQuery } from "~/Hooks/useQuery";

export const ContractDocument = ({
  title,
  documents,
  id,
  type,
  rearrange,
  isMobile,
  data,
  fullEnvelopDetails,
}: {
  title: string;
  documents: any;
  id: any;
  type: "document" | "template" | "url";
  rearrange?: boolean;
  isMobile?: boolean;
  data: any;
  fullEnvelopDetails: any;
}) => {
  const [selectedDocument, setSelectedDocument] = useState<string>("");
  const [loadingDocId, setLoadingDocId] = useState<string>("");
  const [loadingRedirect, setLoadingRedirect] = useState<boolean>();
  const { hasRole } = useHasAccess();
  const query = useQuery();
  const activeTab = query.get("activeTab");

  // logic for hiding first lhdn document if there are more than one
  if (documents) {
    const signedOnPaperDocs = documents?.filter((doc: any) => doc?.name?.startsWith("Signed-on-Paper_"));
    if (signedOnPaperDocs?.length == 2) {
      const indexToRemove = documents?.findIndex((doc: any) => doc?.name?.startsWith("Signed-on-Paper_"));
      if (indexToRemove !== -1) {
        documents?.splice(indexToRemove, 1);
      }
    }
  }

  const safeDocuments = Array.isArray(documents) ? documents : [];
  const overallPdfRegex = /^SPA\d*\(V\d+\)\.pdf$/;
  const sortedData = safeDocuments
    ?.filter((doc: any) => !overallPdfRegex.test(doc.documentName))
    ?.sort((a: any, b: any) => Number(a.documentId) - Number(b.documentId));

  const latestDocument =
    documents && documents.length > 0
      ? documents.reduce((latest: any, current: any) =>
          Number(current.documentId ?? 0) > Number(latest.documentId ?? 0) ? current : latest
        )
      : null;

  const viewDocument = async (envelope: any, isDownload = false) => {
    if (loadingDocId) return;

    const rawDocKey = localStorage.getItem("docVersionKey");
    const docKey = rawDocKey ? JSON.parse(rawDocKey) : "";

    if (isDownload && activeTab === "contractInformation") {
      setLoadingDocId(envelope.documentId);

      try {
        const response = await fetch(envelope.blobUrl + docKey, {
          method: "GET",
        });

        const downloadedData = await response.blob();

        const filename = `${envelope.documentName}.pdf`;

        const downloadLink = document.createElement("a");
        const fileURL = URL.createObjectURL(downloadedData);

        downloadLink.href = fileURL;
        downloadLink.download = filename;
        downloadLink.style.display = "none";

        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);

        URL.revokeObjectURL(fileURL);
      } catch (error) {
        console.error("Error downloading file:", error);
      } finally {
        setLoadingDocId("");
      }

      return;
    }

    if (isDownload) {
      Modal.info({
        title: "Password Protected File",
        content:
          "The attachment is password protected. Please enter the Project ID No. to open it. Example:000123.",
        okText: "Download",
        closable: true,
        async onOk() {
          setLoadingDocId(envelope.documentId);

          try {
            const payload = {
              blobUrl: `${envelope.blobUrl}${docKey}`,
              password: `${data.projectId}`,
              documentName: envelope.documentName,
            };

            const response = await api.post("v1/spa/download-encrypted-url", payload, {
              responseType: "blob",
            });

            const blob = new Blob([response.data], {
              type: "application/pdf",
            });

            const fileURL = URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = fileURL;
            link.download = `${envelope.documentName}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            URL.revokeObjectURL(fileURL);
          } catch (error) {
            console.error("Error downloading file:", error);
          } finally {
            setLoadingDocId("");
          }
        },
      });

      return;
    }

    setLoadingDocId(envelope.documentId);

    try {
      setSelectedDocument(envelope.blobUrl + docKey);
    } finally {
      setLoadingDocId("");
    }
  };

  const handleEdit = async () => {
    setLoadingRedirect(true);
    const formData = new FormData();
    formData.append("envelopeId", id);
    formData.append("returnUrl", window.location.href);
    post("v1/clm/createConsoleView?action=rearrange", formData)
      .then((res: any) => {
        setLoadingRedirect(false);
        window.location.replace(res?.response?.data);
      })
      .catch((err) => {
        setLoadingRedirect(false);
      });
  };
  return (
    <>
      <Row>
        {selectedDocument && (
          <PDFViewer
            show={true}
            title={"View Document"}
            subTitle={""}
            setShow={() => setSelectedDocument("")}
            url={selectedDocument}
          />
        )}
        <Col
          span={24}
          style={{ paddingTop: 10, paddingBottom: 0 }}
        >
          <Row>
            <Col>
              <Typography.Title
                level={isMobile ? 5 : 4}
                style={{ marginBottom: 24 }}
              >
                {title}
              </Typography.Title>
            </Col>
            {hasRole(["*"]) && (
              <Col>
                {rearrange && (
                  <>
                    <Icon
                      icon="carbon:edit"
                      inline
                      className="doc-icon-secondary"
                      fontSize={24}
                      style={{
                        marginLeft: 8,
                        marginRight: 8,
                        cursor: "pointer",
                      }}
                      onClick={() => handleEdit()}
                    />
                    {loadingRedirect && (
                      <Icon
                        icon="eos-icons:bubble-loading"
                        inline
                        className="doc-icon-secondary"
                        fontSize={24}
                        style={{ marginLeft: 12, marginRight: 12 }}
                      />
                    )}
                  </>
                )}
              </Col>
            )}
          </Row>
          {activeTab === "documentVersions"
            ? latestDocument && (
                <div style={{ marginBottom: 10 }}>
                  <Space>
                    <Icon
                      icon="ph:file-pdf"
                      inline
                      fontSize={isMobile ? 20 : 24}
                      className="doc-icon-secondary"
                      cursor="pointer"
                      style={{ marginRight: 8 }}
                      onClick={() => viewDocument(latestDocument)}
                    />

                    {isMobile ? (
                      <Typography.Text style={{ color: "#29256E" }}>{latestDocument?.documentName}</Typography.Text>
                    ) : (
                      <Typography.Title level={5}>{latestDocument?.documentName}</Typography.Title>
                    )}

                    <Icon
                      icon="ic:outline-remove-red-eye"
                      inline
                      fontSize={isMobile ? 20 : 24}
                      className="doc-icon-secondary"
                      cursor="pointer"
                      style={{ marginLeft: 16, marginRight: 8 }}
                      onClick={() => viewDocument(latestDocument)}
                    />

                    <Icon
                      icon="material-symbols:download"
                      inline
                      className="doc-icon-secondary"
                      fontSize={isMobile ? 20 : 24}
                      style={{ marginLeft: 8, marginRight: 8 }}
                      cursor="pointer"
                      onClick={() => viewDocument(latestDocument, true)}
                    />

                    {loadingDocId === latestDocument.documentId && (
                      <Icon
                        icon="eos-icons:bubble-loading"
                        inline
                        className="doc-icon-secondary"
                        fontSize={24}
                        style={{ marginLeft: 12, marginRight: 12 }}
                      />
                    )}
                  </Space>
                </div>
              )
            : !isEmpty(sortedData) &&
              sortedData?.map((item: any, index: number) => (
                <div
                  key={index}
                  style={{ marginBottom: 10 }}
                >
                  <Space>
                    <Icon
                      icon="ph:file-pdf"
                      inline
                      fontSize={24}
                      className="doc-icon-secondary"
                      cursor="pointer"
                      style={{ marginRight: 8 }}
                      onClick={() => viewDocument(item)}
                    />

                    <Typography.Title level={5}>{item?.documentName}</Typography.Title>

                    <Icon
                      icon="ic:outline-remove-red-eye"
                      inline
                      fontSize={24}
                      className="doc-icon-secondary"
                      cursor="pointer"
                      style={{ marginLeft: 16, marginRight: 8 }}
                      onClick={() => viewDocument(item)}
                    />

                    <Icon
                      icon="material-symbols:download"
                      inline
                      className="doc-icon-secondary"
                      fontSize={24}
                      style={{ marginLeft: 8, marginRight: 8 }}
                      cursor="pointer"
                      onClick={() => viewDocument(item, true)}
                    />

                    {loadingDocId === item.documentId && (
                      <Icon
                        icon="eos-icons:bubble-loading"
                        inline
                        className="doc-icon-secondary"
                        fontSize={24}
                        style={{ marginLeft: 12, marginRight: 12 }}
                      />
                    )}
                  </Space>
                </div>
              ))}
        </Col>
      </Row>
    </>
  );
};
